// Recarrega o app quando um import() de tela falha porque o chunk hasheado sumiu
// após um deploy (aba aberta antes da nova versão). Em vez do crash "Failed to
// fetch dynamically imported module", mostra "Atualizando…" e dá reload.

import { type ComponentProps, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorComponent, useRouterState } from "@tanstack/react-router";
import { AppUpdateCard, AppUpdatingScreen } from "@organizacaox/lai-design-system";
import { Logo } from "../brand.js";
import { Shimmer } from "@/components/ui/shimmer";
import {
  RELOAD_GUARD_KEY,
  RELOAD_WINDOW_MS,
  STALE_BOOT_RESTART_GUARD_KEY,
  VERSION_RELOAD_GUARD_KEY,
  clearCacheAndRestart,
  isChunkLoadError,
  readReloadGuard,
  shouldReload,
  writeReloadGuard,
} from "./app-recovery.js";
import { AppErrorFallback } from "./app-error-boundary.js";
import {
  CONTROLLER_WAIT_MS,
  applyUpdate,
  checkForSwUpdate,
  hasServiceWorkerControl,
  notifyUpdateReady,
  onUpdateReady,
} from "./app-sw-update.js";
import { BUILD_ID, fetchDeployedBuildId } from "./app-version.js";
import { isCallWorkspacePath } from "./call-workspace.js";
import { useCallPresence } from "./use-unload-guard.js";
import "../i18n.js";

export { RELOAD_GUARD_KEY, RELOAD_WINDOW_MS, isChunkLoadError, shouldReload };
// Depois que a pessoa escolhe atualizar, deixa a tela de progresso aparecer
// antes do reload. A detecção sozinha nunca inicia este atraso.
export const RELOAD_DELAY_MS = 1000;
// Teto para a atualização inteira. Derivado do caminho real, não de um número
// redondo: RELOAD_DELAY_MS até chamar `applyUpdate` + CONTROLLER_WAIT_MS que ele
// espera o SW novo assumir a aba, e uma folga. Passou disso sem o reload ter
// acontecido, é porque `applyUpdate` está pendurado (registro do SW que nunca
// resolve) — a tela vira a variante "failed" em vez de deixar o vendedor preso
// num spinner sem saída. Um teto muito maior que o caminho real seria decorativo:
// nunca dispararia.
export const UPDATE_TIMEOUT_MS = RELOAD_DELAY_MS + CONTROLLER_WAIT_MS + 2000;
export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
// Janela logo após abrir/recarregar a aba em que a versão nova é aplicada sem
// perguntar. Em modo "prompt" o reload manual é respondido pelo SW velho com o
// index.html antigo, e o SW novo só assume com o clique no card — quem acabou
// de recarregar esperava justamente a versão nova. Cobre o SW que já estava em
// `waiting` (sinal imediato) e a instalação disparada pela própria navegação.
export const BOOT_AUTO_UPDATE_MS = 15 * 1000;
export const VERSION_CHECK_MIN_GAP_MS = 30 * 1000;
// Teto para o SW novo ficar pronto quando a aba ABRIU velha (a sondagem da
// abertura já viu outra versão publicada). A tela fica em "Atualizando…" nesse
// meio-tempo; passou disso, apaga o cache e recarrega da rede.
export const BOOT_STALE_MAX_WAIT_MS = 30 * 1000;
// Um reinício desses por janela. A guarda de 10 s do reload não serve aqui: ela
// já expirou quando o teto de 30 s chega, e se /version.json e o index.html
// discordarem (cache no caminho) a aba limparia o cache a cada 30 s.
export const STALE_BOOT_RESTART_WINDOW_MS = 5 * 60 * 1000;

// A marca é do produto: o design system entrega a interface da atualização, não
// o logo. Na variante "failed" ele sai de cena para o texto do erro.
const updatingLogo = <Logo className="size-20 animate-pulse motion-reduce:animate-none" />;

// O AppUpdatingScreen não expõe o texto de "Atualizando…" para customizar: o
// shimmer entra junto do logo (visual, aria-hidden) e o <p> do design system
// vira sr-only — continua sendo o que a região viva anuncia. Na variante
// "failed" o ícone não é renderizado e os <p> são o erro, então nada muda.
function UpdatingScreen({
  failed,
  ...props
}: { failed: boolean } & Omit<ComponentProps<typeof AppUpdatingScreen>, "variant" | "icon">) {
  const { t } = useTranslation("lai");
  return (
    <AppUpdatingScreen
      {...props}
      variant={failed ? "failed" : "updating"}
      className={failed ? undefined : "[&>p:first-of-type]:sr-only"}
      icon={
        <>
          {updatingLogo}
          <Shimmer aria-hidden="true" duration={1800} className="text-sm text-muted-foreground">
            {t("appUpdate.updating")}
          </Shimmer>
        </>
      }
    />
  );
}

// Error component do router (registrado como defaultErrorComponent). É AQUI que
// um import() de tela lazy que falhou — chunk hasheado que sumiu após um deploy
// — chega de forma determinística: o CatchBoundary global do TanStack Router
// fica DENTRO do RouterProvider, então um error boundary externo nunca veria
// esse erro. Em produção, erro de chunk → tela "Atualizando…" + reload (guardado
// contra loop). Qualquer outro erro (e tudo em dev) cai no ErrorComponent padrão
// do router — mesmo comportamento de antes.
export function AppErrorScreen({ error }: { error: unknown }) {
  const isUpdate = import.meta.env.PROD && isChunkLoadError(error);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isUpdate) return;
    const now = Date.now();
    if (shouldReload(now, readReloadGuard(RELOAD_GUARD_KEY))) {
      writeReloadGuard(RELOAD_GUARD_KEY, now);
      const timer = setTimeout(() => location.reload(), RELOAD_DELAY_MS);
      return () => clearTimeout(timer); // limpa se desmontar antes do reload
    }
    setFailed(true); // reload já falhou dentro da janela: para de loopar
  }, [isUpdate]);

  const retry = () => {
    writeReloadGuard(RELOAD_GUARD_KEY, Date.now());
    location.reload();
  };

  if (isUpdate) {
    return (
      <UpdatingScreen
        failed={failed}
        onRetry={retry}
        onClearCache={() => void clearCacheAndRestart()}
      />
    );
  }
  // Em dev, o ErrorComponent do router (stack trace) é mais útil. Em produção,
  // a tela padrão do app — com a saída "limpar cache e reiniciar".
  if (import.meta.env.DEV) return <ErrorComponent error={error} />;
  return <AppErrorFallback error={error} />;
}

// Montado na raiz do router: quando o SW avisa que há build nova, mantém o app
// em uso e oferece a atualização num card flutuante. Só o clique da pessoa
// ativa o SW novo e recarrega a aba. Nas telas de discagem o card não aparece:
// ele volta quando o vendedor sai delas.
//
// A guarda anti-loop continua necessária: se o `skipWaiting` não pegar, o SW
// novo continua em `waiting`,
//    o `onNeedRefresh` chega de novo depois do reload e o app entraria num
//    ciclo de reload a cada poucos segundos. A guarda em sessionStorage
//    (VERSION_RELOAD_GUARD_KEY) corta esse ciclo na segunda passada e mostra a
//    variante "failed" — a mesma defesa que o AppErrorScreen faz para o chunk
//    faltante, com chave própria para não misturar os dois caminhos.
export function AppUpdateWatcher() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [ready, setReady] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [failed, setFailed] = useState(false);
  const [autoUpdate, setAutoUpdate] = useState(false);
  // A primeira sondagem de /version.json, feita ao abrir, encontrou outra versão
  // publicada: a aba carregou o index.html velho do SW (F5 logo depois de um
  // deploy). Não há trabalho a perder, então a tela fica em "Atualizando…" até o
  // SW novo ficar pronto e nenhum clique cancela. Sem isto, um clique ou tecla
  // antes do precache novo (~8 MB) terminar encerrava a janela de abertura e a
  // pessoa caía no card (reproduzido no navegador em 25/09).
  const [staleOnBoot, setStaleOnBoot] = useState(false);

  // Enquanto a aba está "recém-aberta" (dentro da janela e sem clique/tecla),
  // não há trabalho a perder: a versão nova entra sem card. O primeiro gesto
  // encerra a janela — dali em diante só o clique da pessoa atualiza.
  const [pristine, setPristine] = useState(true);
  useEffect(() => {
    const end = () => setPristine(false);
    const timer = setTimeout(end, BOOT_AUTO_UPDATE_MS);
    window.addEventListener("pointerdown", end, { capture: true, once: true });
    window.addEventListener("keydown", end, { capture: true, once: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pointerdown", end, { capture: true });
      window.removeEventListener("keydown", end, { capture: true });
    };
  }, []);

  useEffect(() => onUpdateReady(() => setReady(true)), []);

  // O SW pode levar horas para ser consultado novamente numa SPA. O JSON é uma
  // sondagem barata, mas não anuncia atualização antes do precache estar pronto
  // quando esta aba já é controlada por um SW.
  useEffect(() => {
    let cancelled = false;
    let lastCheck = 0;
    let initial = true;
    const controller = new AbortController();
    const check = async (force = false) => {
      if (cancelled || document.visibilityState !== "visible") return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      const now = Date.now();
      if (!force && now - lastCheck < VERSION_CHECK_MIN_GAP_MS) return;
      lastCheck = now;
      const atOpening = initial;
      initial = false;
      const deployed = await fetchDeployedBuildId(controller.signal);
      if (cancelled || deployed === null || deployed === BUILD_ID) return;
      if (atOpening) setStaleOnBoot(true);
      if (hasServiceWorkerControl()) checkForSwUpdate();
      else notifyUpdateReady();
    };
    const onVisible = () => void check(true);
    const onOnline = () => void check(true);
    void check(true);
    const interval = window.setInterval(() => void check(true), VERSION_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("online", onOnline);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  // O Vite dispara este evento antes de uma rejeição de import em alguns
  // navegadores. Transformamos ambos em um único sinal idempotente.
  useEffect(() => {
    const onPreloadError = (event: Event) => {
      event.preventDefault();
      notifyUpdateReady();
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) notifyUpdateReady();
    };
    window.addEventListener("vite:preloadError", onPreloadError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("vite:preloadError", onPreloadError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  // Duas perguntas diferentes: `naRotaDeLigacao` é ONDE o vendedor está (telas
  // de discagem manual/automática, campanhas e click-to-call — o card cobriria o
  // atendimento); `emLigacaoAgora` é se existe chamada no ar em qualquer rota.
  // Em ambos os casos não há card, porque atualizar recarrega a aba e o
  // `pagehide` do softphone derruba a ligação com o cliente na linha.
  const naRotaDeLigacao = isCallWorkspacePath(pathname);
  const emLigacaoAgora = useCallPresence().inCall;
  const abriuVelha = staleOnBoot && !emLigacaoAgora;

  const startUpdate = () => {
    const now = Date.now();
    setUpdating(true);
    if (!shouldReload(now, readReloadGuard(VERSION_RELOAD_GUARD_KEY))) {
      setFailed(true);
      return;
    }
    writeReloadGuard(VERSION_RELOAD_GUARD_KEY, now);
  };

  // Aplicação automática na janela de abertura — ou, se a aba abriu velha, a
  // qualquer momento até o teto. Com ligação no ar, nunca. Se a guarda anti-loop
  // recusar (o reload anterior não trocou a versão), não vira tela de erro: cai
  // no card, e a pessoa decide.
  useEffect(() => {
    if (!ready || updating || autoUpdate || emLigacaoAgora) return;
    if (!pristine && !abriuVelha) return;
    setAutoUpdate(true);
    if (!shouldReload(Date.now(), readReloadGuard(VERSION_RELOAD_GUARD_KEY))) {
      setStaleOnBoot(false);
      return;
    }
    startUpdate();
  }, [ready, pristine, updating, autoUpdate, emLigacaoAgora, abriuVelha]);

  // Chamada no ar vence a atualização: a tela de "Atualizando…" sai da frente e
  // a versão nova volta a ser o card de sempre.
  useEffect(() => {
    if (emLigacaoAgora) setStaleOnBoot(false);
  }, [emLigacaoAgora]);

  // O SW novo não ficou pronto no teto (rede lenta, instalação travada): apaga o
  // cache e recarrega da rede, que serve o index.html novo. Com guarda própria:
  // se já reiniciou há pouco e a aba segue velha, libera o app com o card.
  useEffect(() => {
    if (!abriuVelha || updating) return;
    const timer = setTimeout(() => {
      const now = Date.now();
      const last = readReloadGuard(STALE_BOOT_RESTART_GUARD_KEY);
      if (last !== null && now - last < STALE_BOOT_RESTART_WINDOW_MS) {
        setStaleOnBoot(false);
        return;
      }
      writeReloadGuard(STALE_BOOT_RESTART_GUARD_KEY, now);
      void clearCacheAndRestart();
    }, BOOT_STALE_MAX_WAIT_MS);
    return () => clearTimeout(timer);
  }, [abriuVelha, updating]);

  useEffect(() => {
    if (!updating || failed) return;
    const apply = setTimeout(() => void applyUpdate(), RELOAD_DELAY_MS);
    // Rede de segurança: se o reload não veio dentro do teto, oferece saída
    // manual em vez de deixar o spinner girando para sempre.
    const bail = setTimeout(() => setFailed(true), UPDATE_TIMEOUT_MS);
    return () => {
      clearTimeout(apply);
      clearTimeout(bail);
    };
  }, [updating, failed]);

  if (abriuVelha && !updating) {
    return <UpdatingScreen failed={false} onRetry={() => void clearCacheAndRestart()} />;
  }
  if (!ready) return null;
  // Na tela de discagem ou com a ligação no ar não se oferece atualizar — e o
  // card volta sozinho quando o vendedor sai da tela ou desliga, sem ter de
  // fazer nada. Esconder é melhor que desabilitar: o AppUpdateCard não tem
  // estado desabilitado, e um botão que não responde ensina menos que um card
  // ausente.
  if (!updating && (naRotaDeLigacao || emLigacaoAgora)) return null;
  if (!updating) return <AppUpdateCard onUpdate={startUpdate} />;
  // O retry NÃO pode ser `location.reload()` puro: chegar aqui significa que o
  // SW novo justamente NÃO assumiu a aba, e numa aba controlada pelo SW velho o
  // reload é respondido pelo index.html do precache VELHO — o mesmo estado, de
  // novo. `clearCacheAndRestart` apaga o Cache Storage e desregistra o SW antes
  // de recarregar, que é o único caminho que realmente sai daqui.
  // Sem `onClearCache`: aqui o próprio retry já limpa o cache, e dois botões com
  // rótulos diferentes para a mesma ação só confundem.
  return <UpdatingScreen failed={failed} onRetry={() => void clearCacheAndRestart()} />;
}
